import api from 'api';
import { useSearchParams } from 'hooks/useSearchParams';
import { useMutation, useQueryClient } from 'react-query';
import { AppError } from 'utils/AppError';
import { ModalActions, salaryComponentParams } from '../constants';
import { SalaryComponent } from '../types';
import listPerquisitesComponents from 'api/gql-utils/perquisitesComponents/listPerquisitesComponents';

type UseModalActionsProps = {
  modalAction: string | null;
  salaryComponent: SalaryComponent;
  onSuccess?: () => void;
  onError?: (error: AppError) => void;
};

export const useModalActions = ({
  modalAction,
  salaryComponent,
  onSuccess,
  onError,
}: UseModalActionsProps) => {
  const { urlParams } = useSearchParams();
  const salaryComponentId = urlParams.get(salaryComponentParams.componentId) || '';
  const queryClient = useQueryClient();

  const actionMutation = useMutation(
    async (params: any): Promise<any> => {
      if (salaryComponent.category === 'PERQUISITES_CATEGORY') {
        const status = params.status === 'SET_STATUS_ACTIVE' ? 'active' : 'inactive';
        return await api.salaryComponents.mutations.editPerquisitesComponent({
          components: [
            {
              id: salaryComponentId,
              name: salaryComponent.name,
              displayName: salaryComponent.displayName,
              description: salaryComponent.description || '',
              showZero: salaryComponent.showZero || false,
              visibleToEmployee: salaryComponent?.visibleToEmployee || false,
              status: status as 'active' | 'inactive',
            },
          ],
        });
      } else if (salaryComponent.category === 'FBP') {
        return await api.flexibleBenefitComponents.mutations.updateFlexibleBenefitStatus({
          status:
            modalAction === ModalActions.enable
              ? 'SET_SALARY_REQUEST_STATUS_ACTIVATE'
              : 'SET_SALARY_REQUEST_STATUS_DEACTIVATE',
          id: salaryComponentId,
        });
      }

      return await api.salaryComponents.mutations.setSalaryComponentStatus(params);
    },
    {
      onSuccess: () => {
        if (salaryComponent.category === 'PERQUISITES_CATEGORY') {
          queryClient.invalidateQueries([listPerquisitesComponents]);
        } else if (salaryComponent.category === 'FBP') {
          queryClient.invalidateQueries([
            api.flexibleBenefitComponents.queries.getFlexibleBenefitsList,
          ]);
        }
        onSuccess && onSuccess();
      },
      onError(error: AppError) {
        onError && onError(error);
      },
    },
  );

  return {
    salaryComponentId,
    modalAction,
    actionMutation,
  };
};
